import { ArrowUpRight } from "lucide-react";
import { PageHero, Reveal, Eyebrow, ArrowButton } from "@/components/bits";

export default function Founders() {
  return (
    <main data-testid="founders-page">
      <section className="bg-tnn-warm">
        <div className="max-w-6xl mx-auto px-6 pt-36 lg:pt-44 pb-14 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <Eyebrow>The Founders</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight text-tnn-ink leading-[1.12]">
                  Built by people who understand healthcare <span className="text-tnn-purple">from the inside.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 text-base sm:text-lg text-tnn-muted leading-relaxed">
                  Tito Ipinmoye and Evi Uwede founded The Nutrition Network Africa in 2021 to carry clinical expertise beyond the
                  clinic, into products, ventures, education and systems.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <div className="bg-white border border-tnn-ink/8 rounded-3xl p-4 shadow-[0_16px_44px_rgba(40,0,40,0.07)]">
                <img
                  src="/assets/founders.webp"
                  alt="Tito Ipinmoye and Evi Uwede, founders of The Nutrition Network Africa"
                  data-testid="founders-hero-image"
                  className="w-full rounded-2xl object-cover aspect-[5/4]"
                />
                <div className="flex items-center justify-between px-2 pt-4 pb-1">
                  <p className="font-display font-semibold text-sm text-tnn-ink">Tito Ipinmoye & Evi Uwede</p>
                  <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-purple">The Founders</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="founder-profiles">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Reveal>
              <article
                data-testid="founder-card-tito"
                className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-8 lg:p-10 h-full transition-colors duration-300 hover:border-tnn-purple/30"
              >
                <img
                  src="/assets/tito.webp"
                  alt="Tito Ipinmoye, Founder of TNN Africa"
                  data-testid="tito-headshot"
                  className="w-24 h-24 rounded-full object-cover border-2 border-tnn-soft"
                />
                <h2 className="mt-6 font-display text-2xl font-bold text-tnn-ink">Tito Ipinmoye</h2>
                <p className="mt-1 text-xs font-bold tracking-[0.18em] uppercase text-tnn-purple">Founder</p>
                <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed">
                  Healthcare innovation executive, product leader and clinical dietitian working at the intersection of
                  nutrition, digital health, AI and healthcare transformation.
                </p>
                <a
                  href="https://www.linkedin.com/in/titoipinmoye/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tito-linkedin"
                  className="group mt-7 inline-flex items-center gap-1.5 bg-tnn-purple text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 hover:bg-tnn-deep"
                >
                  View LinkedIn Profile
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </article>
            </Reveal>
            <Reveal delay={0.1}>
              <article
                data-testid="founder-card-evi"
                className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-8 lg:p-10 h-full transition-colors duration-300 hover:border-tnn-purple/30"
              >
                <img
                  src="/assets/evi.webp"
                  alt="Evi Uwede, Co-Founder of TNN Africa"
                  data-testid="evi-headshot"
                  className="w-24 h-24 rounded-full object-cover border-2 border-tnn-soft"
                />
                <h2 className="mt-6 font-display text-2xl font-bold text-tnn-ink">Evi Uwede</h2>
                <p className="mt-1 text-xs font-bold tracking-[0.18em] uppercase text-tnn-purple">Co-Founder</p>
                <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed">
                  Co-founder contributing to the development of TNN Africa's technology and innovation ecosystem.
                </p>
                <a
                  href="https://www.linkedin.com/in/evi-uwede/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="evi-linkedin"
                  className="group mt-7 inline-flex items-center gap-1.5 bg-tnn-purple text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 hover:bg-tnn-deep"
                >
                  View LinkedIn Profile
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="why-we-built">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal>
              <Eyebrow>Why We Built TNN Africa</Eyebrow>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
                Clinical expertise should not stop at the clinic.
              </h2>
            </Reveal>
            <div>
              <Reveal delay={0.08} className="space-y-4 text-[15px] text-tnn-muted leading-relaxed">
                <p>
                  TNN Africa emerged from a simple recognition: nutrition challenges require more than clinical advice.
                  They require products people can use, services that reach beyond consultation rooms, education that
                  builds lasting capability, and systems designed around the realities of the people they serve.
                </p>
                <p>
                  The organisation exists to translate clinical expertise into those things: products, technology,
                  education and systems-level innovation, so that nutrition knowledge becomes something people can
                  actually benefit from, at scale.
                </p>
                <p>
                  That is why TNN Africa works across the full arc of innovation: understanding problems clinically,
                  designing solutions practically, and building them alongside the partners who can carry them furthest.
                </p>
              </Reveal>
              <Reveal delay={0.16} className="mt-8">
                <ArrowButton to="/contact" testid="founders-cta">Partner With TNN Africa</ArrowButton>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
